import { DecimalPipe, JsonPipe, KeyValuePipe, NgClass, NgForOf, NgIf, NgStyle } from '@angular/common';
import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { eventTypes, messageStatuses } from '../../core/lib/enums';
import { LinqSocketsService } from '../../services';
import { requestAnimFrame } from '../../shared/helpers';
import { Process, SocketsConnection } from '../../shared/interfaces';
import { ProcessClickEvent, ProcessFinishEvent } from '../../shared/models/events';

@Component({
	selector: 'linq-sockets-progress',
	templateUrl: './linq-sockets-progress.component.html',
	styleUrls: ['./linq-sockets-progress.component.scss'],
	standalone: true,
	imports: [NgForOf, NgIf, KeyValuePipe, NgStyle, NgClass, JsonPipe, DecimalPipe],
})
export class LinqSocketsProgressComponent implements OnInit, OnDestroy {
	private readonly destroy$ = new Subject();
	private connections: SocketsConnection[];

	get progress(): { [key: string]: Process } {
		return this.linqSocketsService.progress;
	}

	get numProcesses(): number {
		const num = Object.keys(this.progress).length;
		return num;
	}

	@ViewChild('processContainer') processContainer: ElementRef;
	@ViewChildren('process') processElems: QueryList<ElementRef>;

	@Output('processFinish')
	processFinish = new EventEmitter<ProcessFinishEvent>();
	@Output('processClick') processClick = new EventEmitter<ProcessClickEvent>();

	constructor(readonly elRef: ElementRef, readonly linqSocketsService: LinqSocketsService) {}

	ngOnInit(): void {
		this.init();
	}

	private init(): void {
		this.connections = [];

		// add processFinish listener
		this.linqSocketsService.connectionSubject$
			.pipe(
				// remove this subscription when this component is destroyed
				takeUntil(this.destroy$),
				// only subscribe to events where connection is truthy
				filter(Boolean)
			)
			.subscribe((connection: SocketsConnection) => {
				// try to subscribe to progress messages
				// if successful (aka no other listeners from this component)
				// store connection to unsubscribe when this component is destroyed
				if (connection.subscribe(eventTypes.progress, this.handleProgressMessage)) {
					this.connections.push(connection);
				}
			});
	}

	ngOnDestroy(): void {
		this.destroy();
	}

	private destroy(): void {
		this.destroy$.next(null);
		this.destroy$.complete();

		// remove any sockets event subscriptions that were made by this component
		this.connections.forEach((connection: SocketsConnection) => {
			if (connection) {
				connection.unsubscribe(eventTypes.progress, this.handleProgressMessage);
			}
		});
	}

	// note that this must be an arrow function to preserve 'this'
	private readonly handleProgressMessage = (message: any): void => {
		if (message.Status === messageStatuses.success || message.Status === messageStatuses.error) {
			const processElem = this.processElems.find((elem) => elem.nativeElement.dataset.linqsocketsProgressProcess === message.Id);
			this.finishProcess(this.progress[message.Id], processElem.nativeElement);
		}
	};

	getProgressBarStyle({ Status, frac }: Process): { [key: string]: any } {
		let hue = frac * 120;

		if (Status === messageStatuses.error) {
			hue = 0;
		}

		const background = `hsl(${hue}, 75%, 50%)`;
		const color = '#fff';
		const width = `${Math.ceil(frac * 100)}%`;

		const style = { width, background, color };
		return style;
	}

	clickProcess(process: Process, { target }: Event): void {
		const elem = (target as Element).closest('[data-linqsockets-progress-process]');

		const event = new ProcessClickEvent(process, elem);

		this.processClick.emit(event);

		if (event.isDefaultPrevented()) {
			return;
		}

		// remove process if clicked while waiting for auto dismiss
		if (process.Status === messageStatuses.success || process.Status === messageStatuses.error) {
			this.linqSocketsService.removeProcess(process.Id);
		}
	}

	private finishProcess(process: Process, elem: HTMLElement): void {
		const event = new ProcessFinishEvent(process, elem);

		this.processFinish.emit(event);

		if (event.isDefaultPrevented()) {
			return;
		}

		// start the dismiss timer for this process
		process.lifetime = 10 * 1000;

		const updateLifetime = () => {
			// this process may have been clicked (dismissed) since last frame
			if (!elem || !this.progress[process.Id]) {
				return;
			}

			const now = Date.now();

			// pause the timer if the process is hovered
			const hovered = this.processContainer.nativeElement.querySelector(':hover');

			const isHovered = hovered && hovered.dataset.linqsocketsProgressProcess === elem.dataset.linqsocketsProgressProcess;

			if (!isHovered) {
				const delta = now - process.updatedAt;
				process.lifetime -= delta;
				process.updatedAt = now;
			}

			// otherwise, run again next frame or dismiss process
			if (process.lifetime > 0) {
				requestAnimFrame(updateLifetime);
			} else {
				this.linqSocketsService.removeProcess(process.Id);
			}
		};

		requestAnimFrame(updateLifetime);
	}
}
